import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tables } from "@/lib/api";
import { fmtBRL, elapsedFrom } from "@/lib/format";
import { toast } from "sonner";
import { Cards, Trash, DoorOpen } from "@phosphor-icons/react";

const StatusBadge = ({ status }) => (
    <span
        className={`text-[11px] uppercase tracking-widest px-2 py-1 rounded ${
            status === "active"
                ? "bg-gold/15 text-gold border border-gold/30"
                : "bg-secondary text-muted-foreground border border-border"
        }`}
    >
        {status}
    </span>
);

export default function TablesPage() {
    const [tables, setTables] = useState([]);

    const load = useCallback(async () => {
        const list = await Tables.list();
        const detailed = await Promise.all(list.map(async (t) => {
            try {
                const d = await Tables.get(t.id);
                return { ...t, _stats: d.stats, _wl: d.waitlist?.filter(w => !w.notified).length || 0 };
            } catch {
                return { ...t, _stats: null, _wl: 0 };
            }
        }));
        setTables(detailed);
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [load]);

    const closeTable = async (id) => {
        await Tables.close(id);
        toast.success("Table closed");
        load();
    };
    const removeTable = async (id) => {
        if (!window.confirm("Delete this table and all its history?")) return;
        await Tables.remove(id);
        toast.success("Table deleted");
        load();
    };

    return (
        <div className="space-y-8" data-testid="tables-page">
            <div>
                <h2 className="font-serif text-4xl tracking-tight">Tables</h2>
                <p className="text-muted-foreground text-sm mt-1">All tables — active and closed</p>
            </div>

            {tables.length === 0 && (
                <div className="border border-dashed border-border rounded-md p-10 text-center text-muted-foreground">
                    No tables yet. Head to Reception and open a new table.
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {tables.map((t) => {
                    const seated = t._stats?.seated_count ?? 0;
                    const rake = t._stats?.total_rake ?? 0;
                    const buyins = t._stats?.total_buyins ?? 0;
                    return (
                        <Card key={t.id} data-testid={`card-table-${t.id}`} className="card-lux overflow-hidden">
                            <div className="felt-surface grain px-5 py-4 flex items-center justify-between border-b border-border">
                                <div className="flex items-center gap-3">
                                    <Cards size={22} weight="fill" className="text-gold" />
                                    <div>
                                        <div className="font-serif text-xl">{t.name}</div>
                                        <div className="label-mini">{t.game_type} · {fmtBRL(t.small_blind)}/{fmtBRL(t.big_blind)}</div>
                                    </div>
                                </div>
                                <StatusBadge status={t.status} />
                            </div>
                            <CardContent className="p-5 space-y-4">
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <div className="label-mini">Seated</div>
                                        <div className="metric-num text-2xl">{seated}/{t.max_seats}</div>
                                    </div>
                                    <div>
                                        <div className="label-mini">Buy-in</div>
                                        <div className="metric-num text-xl text-gold">{fmtBRL(buyins)}</div>
                                    </div>
                                    <div>
                                        <div className="label-mini">Rake</div>
                                        <div className="metric-num text-xl text-gold">{fmtBRL(rake)}</div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>Open for {elapsedFrom(t.created_at)}</span>
                                    <span>Queue: {t._wl}</span>
                                </div>
                                <div className="flex items-center gap-2 pt-2">
                                    <Link to={`/tables/${t.id}`} className="flex-1">
                                        <Button data-testid={`btn-open-dealer-${t.id}`} className="btn-gold w-full h-10">Open dealer</Button>
                                    </Link>
                                    {t.status === "active" && (
                                        <Button data-testid={`btn-close-${t.id}`} onClick={() => closeTable(t.id)} variant="outline" className="h-10 border-border">
                                            <DoorOpen size={16} className="mr-1.5" /> Close
                                        </Button>
                                    )}
                                    <Button data-testid={`btn-delete-${t.id}`} onClick={() => removeTable(t.id)} variant="ghost" size="icon" className="text-destructive">
                                        <Trash size={18} />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}
